import { getDomainWorkflowConfig, buildAssetId } from './DomainWorkflowConfig';

//TRD interface
export interface ITRDState {
  selectedType: string;
  selectedCountry: string;
  selectedDriver: string;
  selectedReason: string;
  selectedQueue?: {
    queueId: string;
    name: string;
  };
  typeList: string[];
  reasonList: string[];
  driverList: string[];
  countryList: string[];
  queueList: Array<{ queueId: string; name: string }>;
}

//default TRD interface values
export const DefaultTRDState: ITRDState = {
  selectedType: '',
  selectedCountry: '',
  selectedDriver: '',
  selectedReason: '',
  selectedQueue: undefined,
  typeList: [],
  reasonList: [],
  driverList: [],
  queueList: [],
  countryList: [
    'USA',
    'AUS',
    'DEU',
    'NOR',
    'TUR',
    'NLD',
    'TWN',
    'IND',
    'PHL',
    'NZL',
    'JPN',
    'HKG',
    'ROU',
    'MYS',
    'ISR',
    'LUX',
    'PRT',
    'FIN',
    'CAN',
    'ITA',
    'SWE',
    'CHN',
    'IDN',
    'POL',
    'CZE',
    'ZAF',
    'SVK',
    'ESP',
    'GBR',
    'IRL',
    'CRI',
    'THA',
    'VNM',
    'KOR',
    'AUT',
    'BEL',
    'FRA',
    'SGP'
  ]
};

export interface TRDFields {
  type: string;
  reason: string;
  driver: string;
}

const TRD_ENABLED_DOMAINS: string[] = [
  'MHLS',
  'ATP',
  'BGC',
  'BXT_SE',
  'CTK',
  'DT',
  'NJS',
  'WA',
  'IMH',
  'WHOLE_FOODS'
];

export const isTRDEnabledDomain = (domainId: string) => {
  return TRD_ENABLED_DOMAINS.includes(domainId);
};

export const getDomainSpecificTRDFieldNames = (domain: string): TRDFields => {
  switch (domain) {
    case 'MHLS':
      return {
        type: 'Type',
        reason: 'Reason_for_Contact__c',
        driver: 'contactDriver'
      } as TRDFields;

    case 'OPSHR':
      return {
        type: 'Type',
        reason: 'Reason_for_Contact__c',
        driver: 'contactDriver'
      } as TRDFields;

    default:
      return {
        type: 'Type',
        reason: 'Reason',
        driver: 'Driver'
      } as TRDFields;
  }
};

// Enhanced FilterConfig interface with validation support
export interface FilterConfig {
  key: keyof ITRDState;
  keyList: keyof ITRDState;
  label: string;
  required: boolean;
  validation?: (value: string) => boolean;
  defaultValue?: string;
  fieldMappings?: string[]; // Possible field names for this configuration
}

// Field mapping configuration interface
export interface FieldMappingConfig {
  selectedType: string[];
  selectedReason: string[];
  selectedCountry: string[];
  selectedDriver: string[];
  queueId: string[];
  queueName: string[];
}

// Default field mappings used by all domains unless overridden
const DEFAULT_FIELD_MAPPINGS: FieldMappingConfig = {
  selectedType: ['CaseType', 'Type', 'type'],
  selectedReason: [
    'Reason',
    'ReasonForContact',
    'Reason_for_Contact__c',
    'reason'
  ],
  selectedCountry: ['Country', 'country'],
  selectedDriver: ['ContactDriver', 'Driver', 'driver'],
  queueId: ['queueId', 'QueueId', 'Queue ID', 'queue_id'],
  queueName: ['queueName', 'QueueName', 'Queue Name', 'queue_name']
};

// Domain-specific field mapping overrides (only specify differences from default)
const DOMAIN_FIELD_OVERRIDES: Record<string, Partial<FieldMappingConfig>> = {
  CTK: {
    selectedDriver: [
      'ContactDriver',
      'Driver',
      'driver',
      'CTK_Root_Causes__c',
      'resolver_group'
    ]
  }
};

// Configuration-driven approach for driver filter configurations
const DRIVER_FILTER_CONFIGS: Record<string, FilterConfig[]> = {
  ATP: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    },
    {
      key: 'selectedReason',
      keyList: 'reasonList',
      label: 'reason for contact',
      required: true
    }
  ],
  WHOLE_FOODS: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    },
    {
      key: 'selectedReason',
      keyList: 'reasonList',
      label: 'reason for contact',
      required: true
    }
  ],
  BGC: [],
  BXT_SE: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    }
  ],
  DT: [],
  NJS: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    }
  ],
  WA: [],
  IMH: [],
  MHLS: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    },
    {
      key: 'selectedCountry',
      keyList: 'countryList',
      label: 'country',
      required: true
    }
  ],
  CTK: [],
  OPSHR: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    },
    {
      key: 'selectedReason',
      keyList: 'reasonList',
      label: 'reason for contact',
      required: true
    },
    {
      key: 'selectedCountry',
      keyList: 'countryList',
      label: 'country',
      required: true
    }
  ],
  DEFAULT: [
    {
      key: 'selectedType',
      keyList: 'typeList',
      label: 'type',
      required: true
    },
    {
      key: 'selectedCountry',
      keyList: 'countryList',
      label: 'country',
      required: true
    }
  ]
};

// Configuration validation function
const validateFilterConfig = (config: FilterConfig[]): boolean => {
  return config.every(
    (item) =>
      item.key &&
      item.keyList &&
      item.label &&
      typeof item.required === 'boolean'
  );
};

// Enhanced getDriverFilterConfig and error handling
export const getDriverFilterConfig = (domainId: string): FilterConfig[] => {
  // Get configuration
  const config =
    DRIVER_FILTER_CONFIGS[domainId] || DRIVER_FILTER_CONFIGS.DEFAULT;

  // Validate configuration
  if (!validateFilterConfig(config)) {
    console.error(`Invalid configuration for domain ${domainId}`);
    return DRIVER_FILTER_CONFIGS.DEFAULT;
  }

  // Log warning for fallback to default
  if (!DRIVER_FILTER_CONFIGS[domainId]) {
    console.warn(
      `No specific configuration found for domain ${domainId}, using default`
    );
  }

  return config;
};

// Utility function to get default value for a field
export const getFieldDefaultValue = (
  domainId: string,
  fieldKey: keyof ITRDState
): string | undefined => {
  const config = getDriverFilterConfig(domainId);
  const fieldConfig = config.find((item) => item.key === fieldKey);

  return fieldConfig?.defaultValue;
};

// Utility function to get field mappings for a domain
export const getFieldMappingsForDomain = (
  domainId: string
): FieldMappingConfig => {
  const overrides = DOMAIN_FIELD_OVERRIDES[domainId];
  if (overrides) {
    return { ...DEFAULT_FIELD_MAPPINGS, ...overrides };
  }
  return DEFAULT_FIELD_MAPPINGS;
};

export const getReasonForContactFilterConfig = (domainId: string) => {
  // Get workflow config to check actual dependencies
  const workflowConfig = getDomainWorkflowConfig(domainId);

  // Default to ['type'] if no workflow config exists (backward compatibility)
  const reasonsDependencies = workflowConfig?.steps.reasons.dependencies || [
    'type'
  ];

  // Only show Type filter if reasons depend on type
  if (reasonsDependencies.includes('type')) {
    return [
      {
        key: 'selectedType',
        keyList: 'typeList',
        label: 'type',
        required: true
      }
    ];
  }

  // No filters needed (e.g., WA, DT where reasons only depend on queue)
  return [];
};

/**
 * Generic method to get required table fields based on asset type and domain
 * @param assetType The type of asset ('types', 'reasons', or 'drivers')
 * @param domainId The domain ID
 * @returns Array of table field configurations
 */
export const getRequiredTableFieldsBasedOnDomain = (
  assetType: 'types' | 'reasons' | 'drivers',
  domainId: string
) => {
  const baseFields = {
    type: { header: 'Type', accessor: 'type' },
    reason: { header: 'Reason for Contact', accessor: 'reason' },
    driver: { header: 'Contact Driver', accessor: 'driver' },
    country: { header: 'Country', accessor: 'country' },
    queue: { header: 'Queue', accessor: 'queue' },
    rootCause: { header: 'Root Cause', accessor: 'rootCause' }
  };

  switch (assetType) {
    case 'drivers':
      switch (domainId) {
        case 'CTK':
          return [baseFields.queue, baseFields.rootCause];
        case 'MHLS':
          return [baseFields.type, baseFields.driver, baseFields.country];
        default:
          return [
            baseFields.type,
            baseFields.reason,
            baseFields.driver,
            baseFields.country
          ];
      }

    case 'reasons':
      switch (domainId) {
        case 'MHLS':
          return [baseFields.type, baseFields.reason];
        default:
          return [baseFields.type, baseFields.reason, baseFields.country];
      }

    case 'types':
      switch (domainId) {
        case 'MHLS':
          return [baseFields.type];
        default:
          return [baseFields.type, baseFields.country];
      }

    default:
      return [];
  }
};

export const getRequiredDriverTableFieldsBasedOnDomain = (domainId: string) => {
  return getRequiredTableFieldsBasedOnDomain('drivers', domainId);
};

export const getRequiredReasonForContactsTableFieldsBasedOnDomain = (
  domainId: string
) => {
  return getRequiredTableFieldsBasedOnDomain('reasons', domainId);
};

export const getRequiredTypeTableFieldsBasedOnDomain = (domainId: string) => {
  return getRequiredTableFieldsBasedOnDomain('types', domainId);
};

/**
 * Generic method to map table data based on asset type and domain
 * @param assetType The type of asset ('types', 'reasons', or 'drivers')
 * @param trdState The current TRD state
 * @param domainId The domain ID
 * @param filteredItems The filtered items to map
 * @returns Array of mapped table data
 */
export const listTableDataBasedOnDomain = (
  assetType: 'types' | 'reasons' | 'drivers',
  trdState: ITRDState,
  domainId: string,
  filteredItems: string[]
) => {
  const baseData = {
    type: trdState?.selectedType,
    reason: trdState?.selectedReason,
    country: trdState?.selectedCountry
  };

  switch (assetType) {
    case 'drivers':
      switch (domainId) {
        case 'CTK':
          return filteredItems.map((driver) => ({
            queue: trdState.selectedQueue?.name || '',
            rootCause: driver
          }));
        default:
          return filteredItems.map((driver) => ({
            type: baseData.type,
            reason: baseData.reason,
            country: baseData.country,
            driver: driver
          }));
      }

    case 'reasons':
      switch (domainId) {
        default:
          return filteredItems.map((reason) => ({
            type: baseData.type,
            reason: reason,
            country: baseData.country
          }));
      }

    case 'types':
      switch (domainId) {
        default:
          return filteredItems.map((type) => ({
            type: type,
            reason: baseData.reason,
            country: baseData.country
          }));
      }

    default:
      return [];
  }
};

export const listTableDriversDataBasedOnDomain = (
  trdState: ITRDState,
  _domainId: string,
  filteredDrivers: string[]
) => {
  const baseData = {
    type: trdState?.selectedType,
    reason: trdState?.selectedReason,
    country: trdState?.selectedCountry
  };

  return filteredDrivers.map((driver) => ({
    type: baseData.type,
    reason: baseData.reason,
    country: baseData.country,
    driver: driver
  }));
};

export const listTableReasonForContactsDataBasedOnDomain = (
  trdState: ITRDState,
  _domainId: string,
  filteredReasonForContacts: string[]
) => {
  const baseData = {
    type: trdState?.selectedType,
    reason: trdState?.selectedReason,
    country: trdState?.selectedCountry
  };

  return filteredReasonForContacts.map((reason) => ({
    type: baseData.type,
    reason: reason,
    country: baseData.country
  }));
};

export const listTableTypesDataBasedOnDomain = (
  trdState: ITRDState,
  _domainId: string,
  filteredTypes: string[]
) => {
  const baseData = {
    type: trdState?.selectedType,
    reason: trdState?.selectedReason,
    country: trdState?.selectedCountry
  };

  return filteredTypes.map((type) => ({
    type: type,
    reason: baseData.reason,
    country: baseData.country
  }));
};

/**
 * Generic method to get asset ID based on asset type and domain
 * @param assetType The type of asset ('types', 'reasons', or 'drivers')
 * @param trdState The current TRD state
 * @param domainId The domain ID
 * @returns The asset ID or null if requirements are not met
 */
export const getAssetIdBasedOnDomain = (
  assetType: 'types' | 'reasons' | 'drivers',
  trdState: ITRDState,
  domainId: string
): string | null => {
  const config = getDomainWorkflowConfig(domainId);

  if (config) {
    const stepConfig = config.steps[assetType];

    // Check if queue is required but not selected
    if (stepConfig.requiresQueue && !trdState.selectedQueue?.queueId) {
      return null;
    }

    // Check dependencies
    if (stepConfig.dependencies) {
      if (stepConfig.dependencies.includes('type') && !trdState.selectedType) {
        return null;
      }
      if (
        stepConfig.dependencies.includes('reason') &&
        !trdState.selectedReason
      ) {
        return null;
      }
      if (
        stepConfig.dependencies.includes('country') &&
        !trdState.selectedCountry
      ) {
        return null;
      }
    }

    return buildAssetId(stepConfig.assetIdTemplate, {
      queueId: trdState.selectedQueue?.queueId,
      queueName: trdState.selectedQueue?.name,
      typeName: trdState.selectedType,
      reasonName: trdState.selectedReason,
      countryName: trdState.selectedCountry
    });
  }

  // Fallback to legacy logic
  const getTRDFields = getDomainSpecificTRDFieldNames(domainId);

  switch (assetType) {
    case 'types':
      return getTRDFields.type;

    case 'reasons':
      switch (domainId) {
        case 'MHLS':
        case 'OPSHR':
          return `${getTRDFields.reason}-${getTRDFields.type}:${trdState.selectedType}`;
        default:
          return `${getTRDFields.reason}-${getTRDFields.type}:${trdState.selectedType}`;
      }

    case 'drivers':
      switch (domainId) {
        case 'MHLS':
          return `${getTRDFields.driver}-${getTRDFields.type}:${trdState.selectedType} ${trdState.selectedCountry}`;
        case 'OPSHR':
          return `${getTRDFields.driver}-${getTRDFields.reason}:${trdState.selectedReason}`;
        default:
          return `${getTRDFields.driver}-${getTRDFields.reason}:${trdState.selectedReason}`;
      }

    default:
      return null;
  }
};

//Fetches TRD type assetId key based on domain and workflow config
export const getTypeAssetIdBasedOnDomain = (
  trdState: ITRDState,
  domainId: string
): string | null => {
  return getAssetIdBasedOnDomain('types', trdState, domainId);
};

//Fetches TRD reason assetId key based on domain and workflow config
export const getReasonAssetIdBasedOnDomain = (
  trdState: ITRDState,
  domainId: string
): string | null => {
  return getAssetIdBasedOnDomain('reasons', trdState, domainId);
};

/**
 * Generic utility to create header item based on format type
 * @param label The header label (e.g., 'Type', 'Reason', 'Country')
 * @param accessor The accessor key (defaults to label if not provided)
 * @param format The format type ('simple' for string, 'bulk' for object)
 * @returns Header item in the specified format
 */
const createHeaderItem = (
  label: string,
  accessor?: string,
  format: 'simple' | 'bulk' = 'simple'
) => {
  return format === 'simple'
    ? label
    : { header: label, accessor: accessor || label };
};

/**
 * Generic method to get TRD headers based on format type
 * @param domainId The domain ID
 * @param format The format type ('simple' for string array, 'bulk' for object array)
 * @returns Array of headers in the specified format
 */
export const getTRDHeaders = (
  domainId: string,
  // Format bifurcation required: 'simple' returns string[] for CSV templates/dropdowns,
  // 'bulk' returns {header, accessor}[] for table components/data grids - different consumers need different structures
  format: 'simple' | 'bulk' = 'simple'
) => {
  const headers: any[] = [];
  const workflowConfig = getDomainWorkflowConfig(domainId);

  // Always add Type column - core TRD hierarchy
  headers.push(createHeaderItem('Type', 'Type', format));

  // Add Country if the driver filter config requires it (e.g., MHLS)
  const filterConfig = getDriverFilterConfig(domainId);
  const needsCountry = filterConfig.some(
    (config) => config.key === 'selectedCountry' && config.required
  );
  if (needsCountry) {
    headers.push(createHeaderItem('Country', 'Country', format));
  }

  // Always add Reason column - core TRD hierarchy
  headers.push(createHeaderItem('Reason', 'Reason', format));

  // Always add Driver column - core TRD hierarchy
  headers.push(createHeaderItem('Driver', 'Driver', format));

  // Add Queue Name if any step in the workflow requires a queue
  if (
    workflowConfig?.steps.types.requiresQueue ||
    workflowConfig?.steps.reasons.requiresQueue ||
    workflowConfig?.steps.drivers.requiresQueue
  ) {
    headers.push(createHeaderItem('Queue Name', 'Queue Name', format));
  }

  return headers;
};

//Fetches TRD driver headers as string array
export const getTRDDriverHeaders = (domainId: string) => {
  return getTRDHeaders(domainId, 'simple');
};

//Fetches TRD driver headers as object array for bulk operations
export const getTRDDriverBulkHeaders = (domainId: string) => {
  return getTRDHeaders(domainId, 'bulk');
};

export const getTRDDriverTemplateFileName = (domainId: string) => {
  return `create_or_update_trd_drivers_${domainId.toLowerCase()}_template.csv`;
};

//Fetches TRD driver assetId key based on domain and workflow config
export const getDriverAssetIdBasedOnDomain = (
  trdState: ITRDState,
  domainId: string
): string | null => {
  return getAssetIdBasedOnDomain('drivers', trdState, domainId);
};

/**
 * Utility function to extract field value from driver data with multiple possible field names
 * @param driverDetails The driver data object
 * @param fieldNames Array of possible field names to check
 * @returns The first non-empty value found or empty string
 */
const getFieldValue = (
  driverDetails: Record<string, any>,
  fieldNames: string[]
): string => {
  for (const fieldName of fieldNames) {
    const value = driverDetails[fieldName];
    if (value && typeof value === 'string' && value.trim()) {
      return value.trim();
    }
  }
  return '';
};

/**
 * Simplified function to map driver data to TRD state based on domain configuration
 * @param driverDetails The raw driver data
 * @param domainId The domain ID
 * @returns Populated ITRDState object
 */
export const mapDriverDataToTRDState = (
  driverDetails: Record<string, any>,
  domainId: string
): ITRDState => {
  const filterConfig = getDriverFilterConfig(domainId);
  const fieldMappings = getFieldMappingsForDomain(domainId);
  const trdState = {} as ITRDState;

  // Map fields based on domain configuration
  for (const config of filterConfig) {
    const fieldNames = fieldMappings[config.key as keyof FieldMappingConfig];
    if (fieldNames) {
      (trdState as any)[config.key] = getFieldValue(driverDetails, fieldNames);
    }
  }

  // Always set driver field (mandatory)
  if (!trdState.selectedDriver) {
    trdState.selectedDriver = getFieldValue(
      driverDetails,
      fieldMappings.selectedDriver
    );
  }

  return trdState;
};

/**
 * Simplified function to extract and resolve queue information
 * @param driverDetails The driver data object
 * @param domainId The domain ID
 * @returns Promise resolving to queue object or undefined
 */
export const resolveQueueFromDriverData = async (
  driverDetails: Record<string, any>,
  domainId: string
): Promise<{ queueId: string; name: string } | undefined> => {
  const fieldMappings = getFieldMappingsForDomain(domainId);

  // Extract queue ID and name using domain-specific field mappings
  const queueId = getFieldValue(driverDetails, fieldMappings.queueId);
  const queueName = getFieldValue(driverDetails, fieldMappings.queueName);

  // If we have queue ID, return it with name
  if (queueId) {
    return { queueId, name: queueName || '' };
  }

  // If we only have queue name, look up the ID
  if (queueName) {
    try {
      const GetQueuesBuilder = (await import('../../builders/GetQueuesBuilder'))
        .default;
      const queuesResponse = await GetQueuesBuilder.getQueues(
        domainId,
        undefined,
        queueName
      );
      const matchingQueue = queuesResponse.queues.find(
        (queue) => queue.name === queueName
      );

      if (matchingQueue) {
        return { queueId: matchingQueue.queueId, name: queueName };
      }
    } catch (error) {
      console.warn(`Could not find queue with name: ${queueName}`, error);
    }
  }

  return undefined;
};
